import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sparkles, Brain, FileText, Activity } from "lucide-react";
import { toast } from "sonner";

const TOOLS = [
  { id: "summary", label: "Case summary", icon: FileText, endpoint: "/ai/case-summary", key: "summary", hint: "Paste case-taking notes to get a structured clinical summary." },
  { id: "symptoms", label: "Symptom extraction", icon: Brain, endpoint: "/ai/symptom-extraction", key: "symptoms", hint: "Paste raw notes — extract grouped symptoms." },
  { id: "rx", label: "Rx suggestion", icon: Sparkles, endpoint: "/ai/prescription-suggestion", key: "suggestion", hint: "Suggest 2-3 candidate remedies for a case summary." },
  { id: "sentiment", label: "Sentiment", icon: Activity, endpoint: "/ai/sentiment", key: "sentiment", hint: "Analyse patient communication tone & wellbeing signals." },
];

export default function AiAssist() {
  return (
    <div data-testid="ai-page">
      <div className="mb-7">
        <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[color:var(--muted)]">Powered by Claude Sonnet 4.5</p>
        <h1 className="font-heading text-3xl md:text-4xl text-[color:var(--ink)] font-medium mt-1">AI clinical assist</h1>
      </div>

      <Tabs defaultValue="summary">
        <TabsList className="bg-white border border-[color:var(--border)] rounded-full p-1" data-testid="ai-tabs">
          {TOOLS.map(t => (
            <TabsTrigger key={t.id} value={t.id} className="rounded-full text-xs md:text-sm" data-testid={`ai-tab-${t.id}`}>
              <t.icon className="w-3.5 h-3.5 mr-1.5" /> {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {TOOLS.map(t => (
          <TabsContent key={t.id} value={t.id} className="mt-5">
            <AiTool tool={t} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function AiTool({ tool }) {
  const [text, setText] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!text.trim()) { toast.error("Please enter some text"); return; }
    setLoading(true); setResult("");
    try {
      const { data } = await api.post(tool.endpoint, { text });
      setResult(data[tool.key] || JSON.stringify(data, null, 2));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="bg-white border border-[color:var(--border)] rounded-2xl p-6" data-testid={`ai-input-${tool.id}`}>
        <p className="text-xs text-[color:var(--muted)] mb-3">{tool.hint}</p>
        <Textarea rows={14} value={text} onChange={e => setText(e.target.value)} placeholder="Paste clinical notes here…" data-testid={`ai-textarea-${tool.id}`} />
        <Button onClick={run} disabled={loading} className="mt-4 rounded-full bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid={`ai-run-${tool.id}`}>
          {loading ? "Generating…" : "Run AI"}
        </Button>
      </div>
      <div className="bg-[color:var(--bg-marketing)] border border-[color:var(--border)] rounded-2xl p-6" data-testid={`ai-output-${tool.id}`}>
        <p className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)] mb-3">Result</p>
        {result ? (
          <pre className="text-sm text-[color:var(--ink)] whitespace-pre-wrap font-body leading-relaxed">{result}</pre>
        ) : (
          <p className="text-sm text-[color:var(--muted)]">{loading ? "Working…" : "Output will appear here."}</p>
        )}
      </div>
    </div>
  );
}
